import React from 'react';
import {
  TrendingUp,
  Users,
  Package,
  DollarSign,
  BarChart3,
  Target,
  Layers,
  CheckCircle2,
} from 'lucide-react';
import Card, { CardHeader, CardBody } from '../ui/Card';
import {
  BusinessModelCanvas as BusinessModelCanvasType,
  BusinessModelType,
  CustomerSegmentType,
  DistributionType,
  RevenueType,
  DataSource,
} from '../../types';

interface BusinessModelCanvasProps {
  canvas: BusinessModelCanvasType;
  companyName?: string;
}

const BusinessModelCanvas: React.FC<BusinessModelCanvasProps> = ({
  canvas,
  companyName,
}) => {
  // Helper to format currency
  const formatCurrency = (value?: number): string => {
    if (!value) return 'N/A';
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000_000) {
      return `$${(value / 1_000_000_000).toFixed(1)}B`;
    } else if (absValue >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(1)}M`;
    }
    return `$${value.toLocaleString()}`;
  };

  // Helper to get data source badge color
  const getDataSourceColor = (source: DataSource): string => {
    switch (source) {
      case DataSource.XBRL:
        return 'bg-green-100 text-green-700 border-green-300';
      case DataSource.LLM_ITEM1:
      case DataSource.LLM_MDA:
      case DataSource.LLM_NOTES:
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case DataSource.HYBRID:
        return 'bg-purple-100 text-purple-700 border-purple-300';
      default:
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';
    }
  };

  // Helper to get confidence color
  const getConfidenceColor = (score: number): string => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header - Business Model Type & Quality Metrics */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Layers className="h-8 w-8 text-primary-600" />
              <div>
                <h3 className="text-2xl font-bold text-neutral-900">
                  {companyName || 'Company'} Business Model
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium border border-primary-300">
                    {canvas.business_model_type}
                  </span>
                  <span className="text-xs text-neutral-500">
                    Extracted: {canvas.extraction_timestamp
                      ? new Date(canvas.extraction_timestamp).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quality Metrics */}
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-sm text-neutral-600">Data Completeness</div>
                <div className={`text-2xl font-bold ${getConfidenceColor(canvas.data_completeness ?? 0)}`}>
                  {canvas.data_completeness?.toFixed(1) ?? '0.0'}%
                </div>
              </div>
              <div>
                <div className="text-sm text-neutral-600">XBRL Coverage</div>
                <div className="text-2xl font-bold text-green-600">
                  {canvas.xbrl_coverage?.toFixed(1) ?? '0.0'}%
                </div>
              </div>
              <div>
                <div className="text-sm text-neutral-600">LLM Coverage</div>
                <div className="text-2xl font-bold text-blue-600">
                  {canvas.llm_coverage?.toFixed(1) ?? '0.0'}%
                </div>
              </div>
              <div>
                <div className="text-sm text-neutral-600">Overall Confidence</div>
                <div className={`text-2xl font-bold ${getConfidenceColor(canvas.overall_confidence ?? 0)}`}>
                  {canvas.overall_confidence?.toFixed(1) ?? '0.0'}%
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Value Proposition */}
      {canvas.value_proposition && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-neutral-900">
                Value Proposition
              </h3>
              <span className={`ml-auto px-2 py-1 text-xs rounded border ${getDataSourceColor(canvas.value_proposition.data_source)}`}>
                {canvas.value_proposition.data_source}
              </span>
            </div>
          </CardHeader>
          <CardBody>
            {canvas.value_proposition.headline && (
              <h4 className="text-xl font-semibold text-neutral-900 mb-2">
                {canvas.value_proposition.headline}
              </h4>
            )}
            <p className="text-neutral-700 mb-3">
              {canvas.value_proposition.description}
            </p>
            {canvas.value_proposition.unique_selling_points && canvas.value_proposition.unique_selling_points.length > 0 && (
              <div className="mt-4">
                <h5 className="text-sm font-semibold text-neutral-700 mb-2">
                  Unique Selling Points:
                </h5>
                <ul className="space-y-1">
                  {canvas.value_proposition.unique_selling_points.map((usp, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-neutral-600">
                      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{usp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Products & Services */}
      {canvas.products_services && canvas.products_services.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  Products & Services ({canvas.products_services.length})
                </h3>
              </div>
              <span className="text-sm text-neutral-600">
                Extracted from narrative text
              </span>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {canvas.products_services.map((product) => (
                <div
                  key={product.product_id}
                  className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-neutral-900">{product.name}</h4>
                    {product.is_core_offering && (
                      <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded border border-primary-300">
                        Core
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wide mb-2">
                    {product.category}
                  </div>
                  {product.description && (
                    <p className="text-sm text-neutral-600 mb-3">{product.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-auto pt-2 border-t border-neutral-200">
                    <span className={`text-xs px-2 py-1 rounded border ${getDataSourceColor(product.data_source)}`}>
                      {product.data_source}
                    </span>
                    <span className={`text-xs font-medium ${getConfidenceColor((product.confidence_score ?? 0) * 100)}`}>
                      {((product.confidence_score ?? 0) * 100).toFixed(0)}% conf.
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Customer Segments */}
      {canvas.customer_segments && canvas.customer_segments.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  Customer Segments ({canvas.customer_segments.length})
                </h3>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {canvas.customer_segments.map((segment) => (
                <div
                  key={segment.segment_id}
                  className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-neutral-900">{segment.name}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded border ${
                      segment.segment_type === CustomerSegmentType.B2B
                        ? 'bg-blue-100 text-blue-700 border-blue-300'
                        : segment.segment_type === CustomerSegmentType.B2C
                        ? 'bg-green-100 text-green-700 border-green-300'
                        : 'bg-purple-100 text-purple-700 border-purple-300'
                    }`}>
                      {segment.segment_type}
                    </span>
                  </div>
                  {segment.description && (
                    <p className="text-sm text-neutral-600 mb-3">{segment.description}</p>
                  )}
                  {segment.is_primary_segment && (
                    <div className="inline-flex items-center gap-1 text-xs text-primary-600 mb-2">
                      <CheckCircle2 className="h-3 w-3" />
                      <span>Primary Segment</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-auto pt-2 border-t border-neutral-200">
                    <span className={`text-xs px-2 py-1 rounded border ${getDataSourceColor(segment.data_source)}`}>
                      {segment.data_source}
                    </span>
                    <span className={`text-xs font-medium ${getConfidenceColor((segment.confidence_score ?? 0) * 100)}`}>
                      {((segment.confidence_score ?? 0) * 100).toFixed(0)}% conf.
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Distribution Channels */}
      {canvas.distribution_channels && canvas.distribution_channels.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  Distribution Channels ({canvas.distribution_channels.length})
                </h3>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {canvas.distribution_channels.map((channel) => (
                <div
                  key={channel.channel_id}
                  className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-neutral-900">{channel.name}</h4>
                    {channel.is_primary_channel && (
                      <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded border border-primary-300">
                        Primary
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 uppercase tracking-wide mb-2">
                    {channel.channel_type}
                  </div>
                  {channel.description && (
                    <p className="text-sm text-neutral-600 mb-3">{channel.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-auto pt-2 border-t border-neutral-200">
                    <span className={`text-xs px-2 py-1 rounded border ${getDataSourceColor(channel.data_source)}`}>
                      {channel.data_source}
                    </span>
                    <span className={`text-xs font-medium ${getConfidenceColor((channel.confidence_score ?? 0) * 100)}`}>
                      {((channel.confidence_score ?? 0) * 100).toFixed(0)}% conf.
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Revenue Streams */}
      {canvas.revenue_streams && canvas.revenue_streams.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  Revenue Streams ({canvas.revenue_streams.length})
                </h3>
              </div>
              <span className="text-sm text-neutral-600">
                From XBRL financial data
              </span>
            </div>
          </CardHeader>
          <CardBody>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-3 px-4 font-semibold text-neutral-900">Stream</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-900">Type</th>
                    <th className="text-right py-3 px-4 font-semibold text-neutral-900">Annual Revenue</th>
                    <th className="text-center py-3 px-4 font-semibold text-neutral-900">Recurring</th>
                    <th className="text-center py-3 px-4 font-semibold text-neutral-900">Source</th>
                    <th className="text-right py-3 px-4 font-semibold text-neutral-900">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {canvas.revenue_streams.map((stream) => (
                    <tr key={stream.stream_id} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-neutral-900">{stream.name}</div>
                          {stream.description && (
                            <div className="text-xs text-neutral-500 mt-0.5">{stream.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-700 rounded border border-neutral-300">
                          {stream.revenue_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-neutral-900">
                        {formatCurrency(stream.annual_revenue)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {stream.is_recurring ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600 mx-auto" />
                        ) : (
                          <span className="text-neutral-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-xs px-2 py-1 rounded border ${getDataSourceColor(stream.data_source)}`}>
                          {stream.data_source}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${getConfidenceColor((stream.confidence_score ?? 0) * 100)}`}>
                          {((stream.confidence_score ?? 0) * 100).toFixed(0)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Cost Structure */}
      {canvas.cost_structure && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">
                  Cost Structure
                </h3>
              </div>
              <span className="text-sm text-neutral-600">
                From XBRL financial data
              </span>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {canvas.cost_structure.cost_of_revenue && (
                <div>
                  <div className="text-sm text-neutral-600 mb-1">Cost of Revenue</div>
                  <div className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(canvas.cost_structure.cost_of_revenue)}
                  </div>
                </div>
              )}
              {canvas.cost_structure.research_development && (
                <div>
                  <div className="text-sm text-neutral-600 mb-1">R&D</div>
                  <div className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(canvas.cost_structure.research_development)}
                  </div>
                </div>
              )}
              {canvas.cost_structure.sales_marketing && (
                <div>
                  <div className="text-sm text-neutral-600 mb-1">Sales & Marketing</div>
                  <div className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(canvas.cost_structure.sales_marketing)}
                  </div>
                </div>
              )}
              {canvas.cost_structure.general_administrative && (
                <div>
                  <div className="text-sm text-neutral-600 mb-1">G&A</div>
                  <div className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(canvas.cost_structure.general_administrative)}
                  </div>
                </div>
              )}
              {canvas.cost_structure.capital_expenditures && (
                <div>
                  <div className="text-sm text-neutral-600 mb-1">CapEx</div>
                  <div className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(canvas.cost_structure.capital_expenditures)}
                  </div>
                </div>
              )}
              {canvas.cost_structure.gross_margin != null && (
                <div>
                  <div className="text-sm text-neutral-600 mb-1">Gross Margin</div>
                  <div className="text-2xl font-bold text-green-600">
                    {canvas.cost_structure.gross_margin.toFixed(1)}%
                  </div>
                </div>
              )}
              {canvas.cost_structure.operating_margin != null && (
                <div>
                  <div className="text-sm text-neutral-600 mb-1">Operating Margin</div>
                  <div className="text-2xl font-bold text-green-600">
                    {canvas.cost_structure.operating_margin.toFixed(1)}%
                  </div>
                </div>
              )}
              {canvas.cost_structure.capital_intensity != null && (
                <div>
                  <div className="text-sm text-neutral-600 mb-1">Capital Intensity</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {canvas.cost_structure.capital_intensity.toFixed(1)}%
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default BusinessModelCanvas;
